import type { Appearance } from '@atlaskit/button';
import { useIntl } from '@atlassian/jira-intl';
import type { AnalyticsAttributes } from '@atlassian/jira-onboarding-quickstart-core/src/common/types.tsx';
import {
	useNudgeStep,
	useHasNudgeActive,
	useMountedNudges,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { BUTTON_CLICKED_EVENT_NAME } from '../../common/constants.tsx';
import messages from '../../common/messages.tsx';
import type { NudgeAction } from '../../common/ui/onboarding-spotlight/types.tsx';
import { hiddenNudgeProps } from './constants.tsx';
import type { TourConfig, NudgeTourProps } from './types.tsx';

// A hook to determine props for a tour nudge. Common concerns are the action buttons, actionsBeforeElement, and handlers for outside click and target click
export const useNudgeTour = (
	nudgeId: string,
	{
		tourId,
		steps,
		backMessage,
		nextMessage = messages.nudgeCtaNext,
		doneMessage = messages.nudgeCtaDone,
		hideStepCountForSingleNudge,
		onTourComplete,
		onOutsideClick,
	}: TourConfig,
	isSpotlightTour = false,
): NudgeTourProps => {
	const [nudgeStep, { setNudgeStep }] = useNudgeStep();
	const [hasNudgeActive, { setHasNudgeActive }] = useHasNudgeActive();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	// Check that the nudge should be active, and that the current nudge is the active one.
	if (nudgeStep === '' || hasNudgeActive === false || nudgeId !== nudgeStep) {
		return hiddenNudgeProps;
	}

	// Check that the nudge is even in the steps of the tour config.
	const nudgeIndexInTour = steps.indexOf(nudgeStep);
	if (nudgeIndexInTour === -1) {
		return hiddenNudgeProps;
	}

	const hidden = false;
	const hideStepCount = hideStepCountForSingleNudge && steps.length === 1;
	const nudgeStepCountDisplay = hideStepCount ? '' : `${nudgeIndexInTour + 1}/${steps.length}`;
	const hideNudge = () => {
		setNudgeStep('');
		setHasNudgeActive(false);
	};
	// @ts-expect-error - TS2304 - Cannot find name 'Action'.
	const actions: Array<Action> = [];
	const spotlightActions: Array<NudgeAction> = [];
	// tourId and tourSteps are common analytic attributes that make their way into the button events of the tour nudges (next / back / done) + the common nudge events  (viewed / clicked / outsideClicked).
	// Note that they do not make their way into the hidden event since the tour steps are not computed and there is an early return for hiddenNudgeProps.
	const analyticsAttributes: AnalyticsAttributes = { tourId, tourSteps: steps };

	if (backMessage && nudgeIndexInTour > 0) {
		// If a back action is necessary
		if (isSpotlightTour) {
			const backAction = {
				content: formatMessage(backMessage),
				onClick: () => {
					fireUIAnalytics(
						createAnalyticsEvent({}),
						BUTTON_CLICKED_EVENT_NAME,
						`${nudgeStep}BackButton`,
						analyticsAttributes,
					);
					setNudgeStep(steps[nudgeIndexInTour - 1]);
				},
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				appearance: 'subtle' as Appearance,
			};
			spotlightActions.push(backAction);
		} else {
			const backAction = {
				text: formatMessage(backMessage),
				onClick: () => {
					fireUIAnalytics(
						createAnalyticsEvent({}),
						BUTTON_CLICKED_EVENT_NAME,
						`${nudgeStep}BackButton`,
						analyticsAttributes,
					);
					setNudgeStep(steps[nudgeIndexInTour - 1]);
				},
			};
			actions.unshift(backAction);
		}
	}

	if (nudgeIndexInTour === steps.length - 1) {
		// Last nudge in the tour: needs done action
		const hideNudgeAndCompleteTour = () => {
			hideNudge();
			onTourComplete && onTourComplete();
		};
		const outsideClick = () => {
			hideNudgeAndCompleteTour();
			onOutsideClick && onOutsideClick();
		};
		if (isSpotlightTour) {
			const doneAction = {
				content: formatMessage(doneMessage),
				onClick: () => {
					fireUIAnalytics(
						createAnalyticsEvent({}),
						BUTTON_CLICKED_EVENT_NAME,
						`${nudgeStep}DoneButton`,
						analyticsAttributes,
					);
					hideNudgeAndCompleteTour();
				},
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				appearance: 'default' as Appearance,
			};
			spotlightActions.push(doneAction);
		} else {
			const doneAction = {
				text: formatMessage(doneMessage),
				onClick: () => {
					fireUIAnalytics(
						createAnalyticsEvent({}),
						BUTTON_CLICKED_EVENT_NAME,
						`${nudgeStep}DoneButton`,
						analyticsAttributes,
					);
					hideNudgeAndCompleteTour();
				},
			};
			actions.unshift(doneAction);
		}

		return {
			hidden,
			actions,
			spotlightActions, // adjusted actions for spotlight
			actionsBeforeElement: nudgeStepCountDisplay,
			onOutsideClick: outsideClick,
			setHidden: hideNudgeAndCompleteTour,
			analyticsAttributes,
		};
	}

	const showNextNudge = () => {
		if (nudgeIndexInTour < steps.length - 1) {
			setNudgeStep(steps[nudgeIndexInTour + 1]);
		}
	};

	// First or middle nudges in tour: needs next action
	if (isSpotlightTour) {
		const nextAction = {
			content: formatMessage(nextMessage),
			onClick: () => {
				fireUIAnalytics(
					createAnalyticsEvent({}),
					BUTTON_CLICKED_EVENT_NAME,
					`${nudgeStep}NextButton`,
					analyticsAttributes,
				);
				showNextNudge();
			},
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			appearance: 'default' as Appearance,
		};
		spotlightActions.push(nextAction);
	} else {
		const nextAction = {
			text: formatMessage(nextMessage),
			onClick: () => {
				fireUIAnalytics(
					createAnalyticsEvent({}),
					BUTTON_CLICKED_EVENT_NAME,
					`${nudgeStep}NextButton`,
					analyticsAttributes,
				);
				showNextNudge();
			},
		};
		actions.unshift(nextAction);
	}
	const hideNudgeAndOutsideClick = () => {
		hideNudge();
		onOutsideClick && onOutsideClick();
	};

	return {
		hidden,
		actions,
		spotlightActions, // adjusted actions for spotlight
		actionsBeforeElement: nudgeStepCountDisplay,
		onOutsideClick: hideNudgeAndOutsideClick,
		setHidden: (event: Event) => {
			// This stops the click to the underlying nudge target.
			// This was done since clicking some nudge targets change the UI of the page, which can affect what's being presented in the tour.
			event.stopPropagation();
			showNextNudge();
		},
		analyticsAttributes,
	};
};

// A hook to match the desired step nudges against the mounted nudges. Can be used before the useNudgeTour hook above on the config steps, in order to dynamically determine the available steps.
export const useNudgeTourSteps = (nudgeId: string, steps: string[]): string[] => {
	const [nudgeStep] = useNudgeStep();
	const [mountedNudges] = useMountedNudges();

	// If this nudge is not the active one, avoid the computation of checking desired nudges and the mounting nudge overlap.
	if (nudgeId !== nudgeStep) {
		return [];
	}

	return steps.filter((tourNudge) => mountedNudges.includes(tourNudge));
};
